import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Plus, X, ChevronDown, ChevronRight, AlertTriangle, Sparkles, Paperclip } from 'lucide-react';
import api from '../services/api';
import { useAuth } from '../context/AuthContext';
import AutocompleteProducto from './AutocompleteProducto';
import AutocompleteCategoria from './AutocompleteCategoria';
import MiniModal from './MiniModal';
import { formatMonto, formatFecha, hoyCaracas } from '../utils/format';
import { pref, aVista, MONEDA_LABEL } from '../utils/moneda';
import { tipoPersona, valoresIslr, TIPO_PERSONA_LABEL, retencionesConfiguradas } from '../utils/retenciones';
import { filaClickeable, FOCO_FILA } from '../utils/filaClickeable';

// ============================================================
// Formulario de carga/edición de factura, compartido por la venta
// (pantalla propia, /facturas/venta/...) y la compra (modal dentro del
// estado de cuenta del proveedor). El orden sigue el de una factura de
// QuickBooks: a quién se le factura, datos del documento, líneas, notas,
// totales; el desglose fiscal y la retención van al final porque son
// para el libro, no para leer la factura.
//
// Notas de crédito/débito NO se cargan acá desde cero: se entra con
// `facturaAfectada` ya elegida (ver NotaCreditoDebito en el estado de
// cuenta), porque una nota siempre se hace sobre un documento anterior.
// ============================================================


const LINEA_VACIA = { producto_id: null, categoria_id: '', descripcion: '', cantidad: '1', precio_unitario: '', exento: false };

// Días de crédito <-> fecha de vencimiento (selector de calendario, como en QB).
// Se opera sobre strings "YYYY-MM-DD" para no arrastrar husos horarios.
function diasEntre(fechaIni, fechaFin) {
  const a = new Date(`${fechaIni}T00:00:00`);
  const b = new Date(`${fechaFin}T00:00:00`);
  return Math.round((b - a) / 86400000);
}

function sumarDias(fecha, dias) {
  const d = new Date(`${fecha}T00:00:00`);
  d.setDate(d.getDate() + Number(dias));
  return d.toISOString().slice(0, 10);
}

// Datos fiscales — #34 (Libro de Compras/Ventas). es_fiscal=false = documento
// "por fuera": no le aplica IVA/retención/IGTF y queda fuera del libro SENIAT.
// Una factura nueva arranca destildada: el bloque fiscal se despliega recién
// cuando el usuario marca "Documento fiscal".
const FISCAL_VACIO = {
  es_fiscal: false, tipo_documento: 'factura', documento_afectado_id: '',
  numero_control: '', tipo_operacion: 'interna',
  base_imponible: '', monto_exento: '', alicuota_iva: '', monto_iva: '',
  aplicaIgtf: false, base_igtf: '', alicuota_igtf: '', monto_igtf: '',
  // Retención manual — solo se sigue usando para parte==='cliente' (venta):
  // el agente de retención es el cliente, el comprobante lo emite él y acá
  // se transcribe (la fecha y el monto se proponen, el número se teclea).
  mostrarRetencion: false, retencion_fecha: '', retencion_numero_comprobante: '', retencion_iva_monto: '',
  // Retención calculada — solo parte==='proveedor' (compra), #35/#97.
  retencion_iva_aplica: true, retencion_iva_porcentaje: '75',
  retencion_islr_codigo_id: '', retencion_islr_porcentaje: '', retencion_islr_sustraendo: '',
  retencion_municipal_aplica: true, retencion_municipal_porcentaje: '2',
  retenciones: []
};

// Aviso de precio (#133): si el precio de una línea se aleja más de este %
// del último costo registrado del producto, se marca en la línea.
const UMBRAL_VARIACION_PRECIO = 15;

// Propuesta de la IA (/compras/extraer-ia) -> estado inicial del formulario.
// Devuelve también si los montos fiscales del papel no cuadran con la
// fórmula (líneas − exento, × alícuota), en cuyo caso se abre en manual
// con los valores del papel.
function formDesdeExtraccion(d, alicuotaEmpresa) {
  const lineas = (d.lineas || []).map((l) => ({
    producto_id: l.producto_id_sugerido || null,
    categoria_id: '',
    descripcion: l.descripcion || '',
    cantidad: l.cantidad != null ? String(l.cantidad) : '1',
    precio_unitario: l.precio_unitario != null ? String(l.precio_unitario) : '',
    exento: !!l.exento,
    unidad_ia: l.unidad_medida || null
  }));
  const esFiscal = !!(d.numero_control || Number(d.monto_iva) > 0);
  const suma = lineas.reduce((s, l) => s + (Number(l.cantidad) || 0) * (Number(l.precio_unitario) || 0), 0);
  const exento = lineas.filter((l) => l.exento).reduce((s, l) => s + (Number(l.cantidad) || 0) * (Number(l.precio_unitario) || 0), 0);
  const baseAuto = Math.max(suma - exento, 0);
  const alic = d.alicuota_iva != null ? Number(d.alicuota_iva) : alicuotaEmpresa;
  const manual = esFiscal && (
    (d.base_imponible != null && Math.abs(Number(d.base_imponible) - baseAuto) > 0.01) ||
    Math.abs(alic - alicuotaEmpresa) > 0.01 ||
    (d.monto_iva != null && Math.abs(Number(d.monto_iva) - (baseAuto * alicuotaEmpresa) / 100) > 0.01)
  );
  return {
    manual,
    campos: {
      numero: d.numero || '',
      moneda: d.moneda === 'BS' ? 'BS' : 'USD',
      lineas: lineas.length ? lineas : [{ ...LINEA_VACIA }],
      es_fiscal: esFiscal,
      numero_control: d.numero_control || '',
      ...(manual ? {
        monto_exento: String(d.monto_exento ?? exento),
        base_imponible: String(d.base_imponible ?? baseAuto),
        alicuota_iva: String(alic),
        monto_iva: String(d.monto_iva ?? 0)
      } : {})
    }
  };
}

export const TIPO_DOCUMENTO_LABEL = { factura: 'Factura', nota_debito: 'Nota de débito', nota_credito: 'Nota de crédito' };

const CONFIG_PARTE = {
  cliente: {
    api: '/facturas', param: 'cliente_id', catTipo: 'ingreso', label: 'Cliente',
    autoNumero: true, usaCanal: true, doc: 'factura',
    catPlaceholder: 'Categoría de venta…', numeroLabel: 'Factura #'
  },
  proveedor: {
    api: '/compras', param: 'proveedor_id', catTipo: 'egreso', label: 'Proveedor',
    autoNumero: false, usaCanal: false, doc: 'factura de compra',
    catPlaceholder: 'Categoría del gasto…', numeroLabel: 'N° del proveedor'
  }
};

export default function FacturaForm({
  parte = 'cliente',
  entidad,
  facturaId = null,
  tipoDocumento = 'factura',
  facturaAfectada = null,
  esRestaurante = false,
  enPantalla = false,
  // Propuesta de la IA (#133) para precargar una factura de compra nueva:
  // { archivo, nombre_archivo, datos, avisos, duplicado, categoria_sugerida_id }.
  extraccion = null,
  // Precarga liviana de una plantilla de factura recurrente (#140):
  // { fecha, moneda, dias_credito, lineas }. A diferencia de `extraccion`,
  // no exige enlazar cada línea a un producto (acá puede haber líneas por
  // categoría, ej. alquiler) ni muestra el cartel de "leído por IA".
  prellenado = null,
  onGuardado,
  onCancelar
}) {
  const CP = CONFIG_PARTE[parte] || CONFIG_PARTE.cliente;
  const usaCanal = esRestaurante && CP.usaCanal;
  const { rolActual, empresaActivaData } = useAuth();
  // % municipal propuesto: el personalizado de la empresa o el 2% por defecto
  const pctMunicipalEmpresa = empresaActivaData?.retencion_municipal_pct != null
    ? String(Number(empresaActivaData.retencion_municipal_pct)) : '2';
  // Crear productos ya lo permite el rol operador (igual que facturar); crear
  // categorías está reservado a contador+ en la pantalla de Categorías, así
  // que el atajo de "nueva categoría" solo se ofrece a esos roles.
  const puedeCrearCategoria = ['contador', 'gerente', 'admin'].includes(rolActual);
  const esNota = tipoDocumento !== 'factura';

  const [productos, setProductos] = useState([]);
  const [categorias, setCategorias] = useState([]);
  const [conceptosIslr, setConceptosIslr] = useState([]);
  // ISLR de facturas anteriores (#7 de Armando): concepto habitual del
  // proveedor (se propone en una factura nueva) y último % / sustraendo por
  // concepto (para los del catálogo que no traen % propio).
  const [islrHabitual, setIslrHabitual] = useState(null);
  const islrPropuesto = useRef(false);
  // El concepto de ISLR viene del botón "Retenciones" del proveedor; el
  // desplegable solo aparece si el usuario pide usar otro en esta factura.
  const [elegirIslr, setElegirIslr] = useState(false);
  const tipoProveedor = tipoPersona(entidad?.rif);
  const [formFactura, setFormFactura] = useState(null);
  const [errorFactura, setErrorFactura] = useState('');
  const [guardando, setGuardando] = useState(false);
  const [fiscalAbierto, setFiscalAbierto] = useState(true);
  // Base imponible / alícuota / monto IVA salen por fórmula de las líneas;
  // esto habilita el override manual para cuadrar con el papel del proveedor.
  const [fiscalManual, setFiscalManual] = useState(false);
  const [retencionAbierta, setRetencionAbierta] = useState(true);
  // N° de control correlativo propuesto por el servidor (solo venta): se
  // guarda aparte para poder reponerlo si destildan y vuelven a tildar.
  const [controlSugerido, setControlSugerido] = useState('');
  const [quickProd, setQuickProd] = useState(null);
  const [quickCat, setQuickCat] = useState(null);
  const [quickError, setQuickError] = useState('');
  // Solo compra: último costo por producto (aviso de variación de precio)
  // y categoría habitual del proveedor (se propone al crear un producto).
  const [ultimosCostos, setUltimosCostos] = useState({});
  const [categoriaHabitual, setCategoriaHabitual] = useState(extraccion?.categoria_sugerida_id || '');
  const desdeIA = !!extraccion?.datos && !facturaId;
  const desdePlantilla = !!prellenado && !facturaId;

  const parteId = entidad?.id;
  const categoriasLinea = useMemo(() => categorias.filter((c) => c.tipo === CP.catTipo), [categorias, CP.catTipo]);

  // Tasas del día (solo lectura): la BCV es oficial y nunca se escribe;
  // la de mercado se puede sobreescribir con el campo opcional "Tasa USD".
  const traerTasasDia = useCallback(async (fecha) => {
    try {
      const { data } = await api.get(`${CP.api}/tasas`, { params: { fecha } });
      return { bcv: data.bcv, mercado: data.mercado };
    } catch {
      return { bcv: null, mercado: null };
    }
  }, [CP.api]);

  // ---------- Carga inicial (catálogos + factura a editar / valores nuevos) ----------
  useEffect(() => {
    let vivo = true;
    (async () => {
      const [prod, cat] = await Promise.all([api.get('/productos'), api.get('/categorias')]);
      if (!vivo) return;
      setProductos(prod.data);
      setCategorias(cat.data);
      // Catálogo ISLR: solo hace falta del lado de compra (proveedor).
      if (parte === 'proveedor') {
        api.get('/retencion-islr-codigos').then(({ data }) => vivo && setConceptosIslr(data)).catch(() => {});
        api.get('/compras/retencion-habitual', { params: { proveedor_id: parteId || undefined } })
          .then(({ data }) => vivo && setIslrHabitual(data)).catch(() => {});
        api.get('/compras/ultimos-costos', { params: { excluir_factura_id: facturaId || undefined } })
          .then(({ data }) => vivo && setUltimosCostos(Object.fromEntries(data.map((c) => [c.producto_id, c]))))
          .catch(() => {});
        if (parteId && !extraccion?.categoria_sugerida_id) {
          api.get('/compras/categoria-proveedor', { params: { proveedor_id: parteId } })
            .then(({ data }) => vivo && data.categoria_id && setCategoriaHabitual(data.categoria_id))
            .catch(() => {});
        }
      }

      if (facturaId) {
        const { data: d } = await api.get(`${CP.api}/${facturaId}`);
        const tasasDia = await traerTasasDia(String(d.fecha).slice(0, 10));
        if (!vivo) return;
        // Si lo guardado no coincide con la fórmula (líneas − exento, por la
        // alícuota de la empresa), la factura trae montos cargados a mano: se
        // abre con el override activado para no pisarlos al guardar.
        const totalLineas = d.lineas.reduce((acc, l) => acc + Number(l.cantidad) * Number(l.precio_unitario), 0);
        const exentoGuardado = d.lineas.filter((l) => l.exento).reduce((acc, l) => acc + Number(l.cantidad) * Number(l.precio_unitario), 0);
        const baseAuto = Math.max(totalLineas - exentoGuardado, 0);
        const alicuotaAuto = Number(empresaActivaData?.iva_pct ?? 16);
        setFiscalManual(
          !!(d.es_fiscal !== 0 && d.es_fiscal !== false) && (
            Math.abs(Number(d.base_imponible || 0) - baseAuto) > 0.01 ||
            (d.alicuota_iva != null && Math.abs(Number(d.alicuota_iva) - alicuotaAuto) > 0.01) ||
            Math.abs(Number(d.monto_iva || 0) - (baseAuto * alicuotaAuto) / 100) > 0.01
          )
        );
        setControlSugerido(d.numero_control || '');
        setFormFactura({
          editandoId: d.id, numero: d.numero, fecha: String(d.fecha).slice(0, 10),
          canal: d.canal || '', moneda: d.moneda, indexada: !!Number(d.indexada),
          // si la tasa mercado guardada difiere de la del día, se muestra como override
          tasa_usd: d.tasa_mercado != null && Number(d.tasa_mercado) !== Number(tasasDia.mercado) ? String(d.tasa_mercado) : '',
          tasasDia,
          dias_credito: parte === 'proveedor' ? (d.dias_credito ?? '') : '',
          notas: d.notas || '',
          lineas: d.lineas.length
            ? d.lineas.map((l) => ({
                producto_id: l.producto_id, categoria_id: l.categoria_id || '',
                descripcion: l.descripcion, cantidad: String(l.cantidad),
                precio_unitario: String(l.precio_unitario), exento: !!l.exento
              }))
            : [{ ...LINEA_VACIA }],
          es_fiscal: d.es_fiscal !== 0 && d.es_fiscal !== false,
          tipo_documento: d.tipo_documento || 'factura',
          documento_afectado_id: d.documento_afectado_id || '',
          numero_control: d.numero_control || '',
          tipo_operacion: d.tipo_operacion || 'interna',
          base_imponible: d.base_imponible != null ? String(d.base_imponible) : '',
          monto_exento: d.monto_exento != null ? String(d.monto_exento) : '',
          alicuota_iva: d.alicuota_iva != null ? String(d.alicuota_iva) : '',
          monto_iva: d.monto_iva != null ? String(d.monto_iva) : '',
          aplicaIgtf: d.base_igtf != null,
          base_igtf: d.base_igtf != null ? String(d.base_igtf) : '',
          alicuota_igtf: d.alicuota_igtf != null ? String(d.alicuota_igtf) : '',
          monto_igtf: d.monto_igtf != null ? String(d.monto_igtf) : '',
          // Retención manual (solo venta) — igual que antes.
          mostrarRetencion: parte === 'cliente' && !!(d.retencion_fecha || d.retencion_numero_comprobante || d.retencion_iva_monto),
          retencion_fecha: d.retencion_fecha ? String(d.retencion_fecha).slice(0, 10) : '',
          retencion_numero_comprobante: d.retencion_numero_comprobante || '',
          retencion_iva_monto: d.retencion_iva_monto != null ? String(d.retencion_iva_monto) : '',
          // Retención calculada (solo compra) — precarga desde d.retenciones (#35/#97).
          retencion_iva_aplica: !(d.retenciones || []).length || !!d.retenciones.find((r) => r.tipo === 'iva'),
          retencion_iva_porcentaje: d.retenciones?.find((r) => r.tipo === 'iva')?.porcentaje?.toString() || '75',
          retencion_islr_codigo_id: d.retenciones?.find((r) => r.tipo === 'islr')?.islr_codigo_id || '',
          retencion_islr_porcentaje: d.retenciones?.find((r) => r.tipo === 'islr')?.porcentaje?.toString() || '',
          retencion_islr_sustraendo: d.retenciones?.find((r) => r.tipo === 'islr')?.sustraendo?.toString() || '',
          retencion_municipal_aplica: !(d.retenciones || []).length || !!d.retenciones.find((r) => r.tipo === 'municipal'),
          retencion_municipal_porcentaje: d.retenciones?.find((r) => r.tipo === 'municipal')?.porcentaje?.toString() || pctMunicipalEmpresa,
          retenciones: d.retenciones || []
        });
        return;
      }

      const fechaInicial = desdeIA && /^\d{4}-\d{2}-\d{2}$/.test(extraccion.datos.fecha || '')
        ? extraccion.datos.fecha
        : (desdePlantilla && /^\d{4}-\d{2}-\d{2}$/.test(prellenado.fecha || '') ? prellenado.fecha : hoyCaracas());
      const [prox, tasasDia] = await Promise.all([
        CP.autoNumero ? api.get(`${CP.api}/proximo-numero`) : Promise.resolve({ data: { numero: '', numero_control: '' } }),
        traerTasasDia(fechaInicial)
      ]);
      if (!vivo) return;
      if (desdeIA) {
        const { manual, campos } = formDesdeExtraccion(extraccion.datos, Number(empresaActivaData?.iva_pct ?? 16));
        setFiscalManual(manual);
        setFormFactura({
          editandoId: null, fecha: fechaInicial, canal: '', tasa_usd: '', tasasDia,
          dias_credito: extraccion.datos.dias_credito ?? entidad?.dias_credito_default ?? '',
          notas: '',
          ...FISCAL_VACIO,
          retencion_municipal_porcentaje: pctMunicipalEmpresa,
          ...campos,
          adjunto_archivo: extraccion.archivo, adjunto_nombre: extraccion.nombre_archivo
        });
        return;
      }
      if (desdePlantilla) {
        setFormFactura({
          editandoId: null, numero: '', fecha: fechaInicial, canal: '', moneda: prellenado.moneda || 'USD',
          tasa_usd: '', tasasDia,
          dias_credito: prellenado.dias_credito ?? entidad?.dias_credito_default ?? '',
          notas: '',
          lineas: prellenado.lineas?.length
            ? prellenado.lineas.map((l) => ({
                producto_id: l.producto_id || null, categoria_id: l.categoria_id || '',
                descripcion: l.descripcion || '', cantidad: String(l.cantidad ?? '1'),
                precio_unitario: String(l.precio_unitario ?? ''), exento: !!l.exento
              }))
            : [{ ...LINEA_VACIA }],
          ...FISCAL_VACIO,
          retencion_municipal_porcentaje: pctMunicipalEmpresa
        });
        return;
      }
      setControlSugerido(prox.data.numero_control || '');
      setFormFactura({
        editandoId: null, numero: prox.data.numero, fecha: hoyCaracas(),
        canal: usaCanal ? 'tienda' : '', moneda: facturaAfectada?.moneda || 'USD',
        tasa_usd: '', tasasDia,
        dias_credito: parte === 'proveedor' ? (entidad?.dias_credito_default ?? '') : '',
        notas: esNota && facturaAfectada ? `${TIPO_DOCUMENTO_LABEL[tipoDocumento]} sobre la factura ${facturaAfectada.numero}` : '',
        // Una nota arranca con las líneas de la factura afectada para poder
        // borrar/ajustar solo lo devuelto, en vez de volver a tipear todo.
        lineas: esNota && facturaAfectada?.lineas?.length
          ? facturaAfectada.lineas.map((l) => ({
              producto_id: l.producto_id, categoria_id: l.categoria_id || '',
              descripcion: l.descripcion, cantidad: String(l.cantidad),
              precio_unitario: String(l.precio_unitario), exento: !!l.exento
            }))
          : [{ ...LINEA_VACIA }],
        ...FISCAL_VACIO,
        retencion_municipal_porcentaje: pctMunicipalEmpresa,
        tipo_documento: tipoDocumento,
        documento_afectado_id: facturaAfectada?.id || '',
        // Una nota fiscal nace fiscal: afecta a un documento del libro.
        es_fiscal: esNota ? true : FISCAL_VACIO.es_fiscal,
        numero_control: esNota ? (prox.data.numero_control || '') : ''
      });
    })().catch((err) => {
      if (vivo) setErrorFactura(err.response?.data?.error || 'No se pudo abrir el formulario');
    });
    return () => { vivo = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [facturaId, parteId, tipoDocumento]);

  // % y sustraendo de un concepto de ISLR para este proveedor: los del
  // catálogo según su tipo de persona; si el concepto no los trae, los
  // últimos usados con ese concepto en la empresa.
  const valoresIslrProveedor = useCallback((cod) => {
    const v = valoresIslr(cod, tipoProveedor);
    const ult = cod ? islrHabitual?.por_concepto?.[cod.id] : null;
    const valor = (catalogo, usado) => (catalogo != null ? String(Number(catalogo)) : usado != null ? String(Number(usado)) : '');
    return {
      retencion_islr_porcentaje: valor(v.porcentaje, ult?.porcentaje),
      retencion_islr_sustraendo: valor(v.porcentaje != null ? (v.sustraendo ?? 0) : null, ult?.sustraendo)
    };
  }, [tipoProveedor, islrHabitual]);

  // Factura nueva: las retenciones salen ya puestas y calculadas. Si el
  // proveedor tiene su botón "Retenciones" guardado, mandan esas (IVA,
  // concepto ISLR con el % de su tipo de persona, municipal); si no, se
  // propone el concepto de ISLR de su última factura.
  useEffect(() => {
    if (islrPropuesto.current || !formFactura || formFactura.editandoId || esNota || parte !== 'proveedor') return;
    if (!conceptosIslr.length) return;
    if (retencionesConfiguradas(entidad)) {
      islrPropuesto.current = true;
      const pctIva = Number(entidad.retencion_iva_pct);
      const cod = conceptosIslr.find((c) => c.id === Number(entidad.retencion_islr_codigo_id));
      setFormFactura((f) => ({
        ...f,
        retencion_iva_aplica: pctIva !== 0,
        retencion_iva_porcentaje: pctIva ? String(pctIva) : f.retencion_iva_porcentaje,
        retencion_municipal_aplica: !!entidad.retencion_municipal,
        ...(cod
          ? { retencion_islr_codigo_id: String(cod.id), ...valoresIslrProveedor(cod) }
          : { retencion_islr_codigo_id: '', retencion_islr_porcentaje: '', retencion_islr_sustraendo: '' })
      }));
      return;
    }
    if (!islrHabitual) return;
    islrPropuesto.current = true;
    const h = islrHabitual.proveedor;
    if (!h || formFactura.retencion_islr_codigo_id) return;
    const cod = conceptosIslr.find((c) => c.id === h.islr_codigo_id && c.activo);
    if (!cod) return;
    setFormFactura((f) => (f.retencion_islr_codigo_id ? f : {
      ...f,
      retencion_islr_codigo_id: String(cod.id),
      retencion_islr_porcentaje: h.porcentaje != null ? String(Number(h.porcentaje)) : (cod.porcentaje != null ? String(cod.porcentaje) : ''),
      retencion_islr_sustraendo: h.sustraendo != null ? String(Number(h.sustraendo)) : (cod.sustraendo != null ? String(cod.sustraendo) : '')
    }));
  }, [formFactura, islrHabitual, conceptosIslr, esNota, parte, entidad, valoresIslrProveedor]);

  function cambiarFechaFactura(fecha) {
    setFormFactura((f) => ({ ...f, fecha, tasa_usd: '' }));
    traerTasasDia(fecha).then((tasasDia) => setFormFactura((f) => ({ ...f, fecha, tasasDia })));
  }

  function setLinea(i, cambios) {
    setFormFactura((f) => ({ ...f, lineas: f.lineas.map((l, idx) => (idx === i ? { ...l, ...cambios } : l)) }));
  }
  const agregarLinea = () => setFormFactura((f) => ({ ...f, lineas: [...f.lineas, { ...LINEA_VACIA }] }));
  const quitarLinea = (i) => setFormFactura((f) => ({ ...f, lineas: f.lineas.filter((_, idx) => idx !== i) }));

  function elegirProducto(i, prodId) {
    const p = productos.find((x) => String(x.id) === String(prodId));
    // En una carga por IA se conserva la descripción tal como viene en el
    // papel del proveedor; solo se enlaza el producto.
    const descIA = desdeIA ? formFactura.lineas[i]?.descripcion : '';
    setLinea(i, { producto_id: prodId || null, descripcion: prodId ? (descIA || p?.nombre || '') : descIA });
  }

  // Precio de la línea vs. último costo del producto, en la misma moneda
  // que guarda producto_costo_historial (factura BCV -> Bs a tasa BCV).
  function variacionPrecio(l) {
    const u = l.producto_id && ultimosCostos[l.producto_id];
    const precio = Number(l.precio_unitario);
    if (!u || !(precio > 0) || !(Number(u.costo_unitario) > 0)) return null;
    let comparable = null;
    if (formFactura.moneda === 'USD' && u.moneda === 'USD') comparable = precio;
    else if (formFactura.moneda === 'BS' && u.moneda === 'BS') comparable = precio;
    else if (formFactura.moneda === 'BCV' && u.moneda === 'BS' && Number(formFactura.tasasDia?.bcv) > 0) comparable = precio * Number(formFactura.tasasDia.bcv);
    if (comparable == null) return null;
    const pct = ((comparable - Number(u.costo_unitario)) / Number(u.costo_unitario)) * 100;
    return Math.abs(pct) >= UMBRAL_VARIACION_PRECIO ? { pct, ultimo: u } : null;
  }

  // ---------- Alta rápida de producto / categoría ----------
  async function guardarProductoRapido(e) {
    e.preventDefault();
    setQuickError('');
    const nombre = (quickProd.nombre || '').trim();
    if (!nombre) { setQuickError('Poné un nombre'); return; }
    try {
      const { data } = await api.post('/productos', {
        nombre,
        unidad_medida: quickProd.unidad_medida || 'Unidad',
        disponibilidad: parte === 'proveedor' ? 'compra' : 'venta',
        categoria_id: parte === 'proveedor' ? (quickProd.categoria_id || null) : undefined
      });
      const { data: prods } = await api.get('/productos');
      setProductos(prods);
      const creado = prods.find((p) => String(p.id) === String(data.id));
      setLinea(quickProd.i, {
        producto_id: data.id, categoria_id: '',
        descripcion: desdeIA ? (formFactura.lineas[quickProd.i]?.descripcion || nombre) : (creado?.nombre || nombre)
      });
      setQuickProd(null);
    } catch (err) {
      setQuickError(err.response?.data?.error || 'No se pudo crear el producto');
    }
  }

  async function guardarCategoriaRapida(e) {
    e.preventDefault();
    setQuickError('');
    const nombre = (quickCat.nombre || '').trim();
    if (!nombre) { setQuickError('Poné un nombre'); return; }
    try {
      const { data } = await api.post('/categorias', { nombre, tipo: CP.catTipo });
      const { data: cats } = await api.get('/categorias');
      setCategorias(cats);
      setLinea(quickCat.i, { categoria_id: data.id });
      setQuickCat(null);
    } catch (err) {
      setQuickError(err.response?.data?.error || 'No se pudo crear la categoría');
    }
  }

  // ---------- Totales ----------
  const totalFactura = useMemo(() => {
    if (!formFactura) return 0;
    return formFactura.lineas.reduce((s, l) => s + (Number(l.cantidad) || 0) * (Number(l.precio_unitario) || 0), 0);
  }, [formFactura]);

  const mercadoFactura = formFactura
    ? (Number(formFactura.tasa_usd) > 0 ? Number(formFactura.tasa_usd) : Number(formFactura.tasasDia?.mercado))
    : null;

  // Total pasado a USD real (informativo, para facturas no-USD).
  const totalFacturaUsd = useMemo(() => {
    if (!formFactura || formFactura.moneda === 'USD') return null;
    return aVista(totalFactura, 'USD', formFactura.moneda, Number(formFactura.tasasDia?.bcv), mercadoFactura);
  }, [formFactura, totalFactura, mercadoFactura]);

  // Bloque fiscal por fórmula (#34): el exento es la suma de las líneas
  // marcadas como exentas, la base imponible es el resto y el IVA sale de
  // la alícuota de la empresa. Con `fiscalManual` el usuario sobreescribe
  // los tres para cuadrar con el papel. La vuelta final la hace igual el
  // servidor (nunca se confía a ciegas en lo que manda el frontend).
  const alicuotaEmpresa = Number(empresaActivaData?.iva_pct ?? 16);
  const exentoLineas = formFactura
    ? formFactura.lineas.reduce((acc, l) => acc + (l.exento ? (Number(l.cantidad) || 0) * (Number(l.precio_unitario) || 0) : 0), 0)
    : 0;
  const montoExentoEfectivo = fiscalManual && formFactura?.monto_exento !== ''
    ? Number(formFactura?.monto_exento) || 0
    : exentoLineas;
  const baseImponibleSugerida = formFactura ? Math.max(totalFactura - montoExentoEfectivo, 0) : 0;
  const baseImponibleEfectiva = fiscalManual && formFactura?.base_imponible !== ''
    ? Number(formFactura.base_imponible) || 0
    : baseImponibleSugerida;
  const alicuotaEfectiva = fiscalManual && formFactura?.alicuota_iva !== ''
    ? Number(formFactura.alicuota_iva) || 0
    : alicuotaEmpresa;
  const ivaCalculado = !formFactura || !formFactura.es_fiscal
    ? 0
    : fiscalManual && formFactura.monto_iva !== ''
      ? Number(formFactura.monto_iva) || 0
      : Math.round(((baseImponibleEfectiva * alicuotaEfectiva) / 100) * 100) / 100;

  // Retención que el CLIENTE nos practica sobre el IVA de esta venta: no la
  // emitimos nosotros, así que solo se propone el monto (IVA × 75%) y la
  // fecha; el N° de comprobante se transcribe del papel que manda el cliente.
  const retencionVentaSugerida = Math.round(ivaCalculado * 75) / 100;

  async function guardarFactura(e) {
    e.preventDefault();
    setErrorFactura('');
    // Carga por IA: ninguna línea queda suelta, cada una va a un producto
    // (existente o nuevo confirmado por el usuario) o, si es un gasto sin
    // producto (ej. un servicio de vigilancia), a una categoría (#133).
    if (desdeIA) {
      const sueltas = formFactura.lineas.map((l, i) => (!l.producto_id && !l.categoria_id ? i + 1 : null)).filter(Boolean);
      if (sueltas.length) {
        setErrorFactura(`Enlaza ${sueltas.length === 1 ? 'la línea' : 'las líneas'} ${sueltas.join(', ')} a un producto (existente o nuevo) o elige solo su categoría de gasto.`);
        return;
      }
    }
    setGuardando(true);
    const payload = {
      [CP.param]: parteId,
      numero: formFactura.numero,
      fecha: formFactura.fecha,
      canal: usaCanal ? (formFactura.canal || null) : null,
      moneda: formFactura.moneda,
      tasa_mercado: formFactura.tasa_usd || null,
      dias_credito: parte === 'proveedor' ? (formFactura.dias_credito || null) : undefined,
      notas: formFactura.notas,
      lineas: formFactura.lineas.map((l) => ({
        producto_id: l.producto_id || null,
        categoria_id: l.categoria_id || null,
        descripcion: l.descripcion,
        cantidad: l.cantidad,
        precio_unitario: l.precio_unitario,
        exento: formFactura.es_fiscal ? !!l.exento : false
      })),
      // #151: la base se paga a la BCV del día del pago (solo facturas en Bs).
      indexada: formFactura.moneda === 'BS' && !!formFactura.indexada,
      es_fiscal: formFactura.es_fiscal,
      tipo_documento: formFactura.tipo_documento,
      documento_afectado_id: formFactura.tipo_documento !== 'factura' ? (formFactura.documento_afectado_id || null) : null,
      numero_control: formFactura.numero_control || null,
      tipo_operacion: formFactura.tipo_operacion,
      // Sin override manual se mandan en null y el servidor los deriva de
      // las líneas y del iva_pct de la empresa (misma fórmula que se muestra).
      base_imponible: fiscalManual && formFactura.base_imponible !== '' ? formFactura.base_imponible : baseImponibleSugerida,
      monto_exento: montoExentoEfectivo,
      alicuota_iva: fiscalManual && formFactura.alicuota_iva !== '' ? formFactura.alicuota_iva : null,
      monto_iva: fiscalManual && formFactura.monto_iva !== '' ? formFactura.monto_iva : null,
      base_igtf: formFactura.aplicaIgtf ? (formFactura.base_igtf || 0) : null,
      alicuota_igtf: formFactura.aplicaIgtf ? (formFactura.alicuota_igtf || null) : null,
      monto_igtf: formFactura.aplicaIgtf && formFactura.monto_igtf !== '' ? formFactura.monto_igtf : null,
      retencion_fecha: parte === 'cliente' && formFactura.mostrarRetencion ? (formFactura.retencion_fecha || null) : null,
      retencion_numero_comprobante: parte === 'cliente' && formFactura.mostrarRetencion ? (formFactura.retencion_numero_comprobante || null) : null,
      retencion_iva_monto: parte === 'cliente' && formFactura.mostrarRetencion && formFactura.retencion_iva_monto !== '' ? formFactura.retencion_iva_monto : null,
      // Overrides para el cálculo automático de retención en compra (#35/#97).
      retencion_iva_aplica: parte === 'proveedor' ? formFactura.retencion_iva_aplica : undefined,
      retencion_iva_porcentaje: parte === 'proveedor' ? formFactura.retencion_iva_porcentaje : undefined,
      retencion_islr_codigo_id: parte === 'proveedor' ? (formFactura.retencion_islr_codigo_id || null) : undefined,
      retencion_islr_porcentaje: parte === 'proveedor' ? formFactura.retencion_islr_porcentaje : undefined,
      retencion_islr_sustraendo: parte === 'proveedor' ? formFactura.retencion_islr_sustraendo : undefined,
      retencion_municipal_aplica: parte === 'proveedor' ? formFactura.retencion_municipal_aplica : undefined,
      retencion_municipal_porcentaje: parte === 'proveedor' ? formFactura.retencion_municipal_porcentaje : undefined,
      adjunto_archivo: formFactura.adjunto_archivo || undefined,
      adjunto_nombre: formFactura.adjunto_nombre || undefined
    };
    try {
      const { data } = formFactura.editandoId
        ? await api.put(`${CP.api}/${formFactura.editandoId}`, payload)
        : await api.post(CP.api, payload);
      onGuardado?.(data?.id || formFactura.editandoId);
    } catch (err) {
      setErrorFactura(err.response?.data?.error || `No se pudo guardar la ${CP.doc}`);
      setGuardando(false);
    }
  }

  if (!formFactura) {
    return (
      <p className="text-sm text-slate-400 py-6 text-center">
        {errorFactura || 'Cargando…'}
      </p>
    );
  }

  const facturaMoneda = formFactura.moneda;
  const vencimientoFactura = formFactura.fecha && formFactura.dias_credito !== '' && formFactura.dias_credito != null
    ? sumarDias(formFactura.fecha, formFactura.dias_credito)
    : '';

  return (
    <>
      <form onSubmit={guardarFactura} className="space-y-4">
        {desdeIA && (
          <div className="rounded-md border border-teal-200 bg-teal-50 p-3 space-y-1.5 text-xs">
            <p className="flex items-center gap-1.5 text-teal-800 font-medium">
              <Sparkles size={14} /> Datos leídos por IA — revisalos antes de guardar
              <span className="ml-auto flex items-center gap-1 font-normal text-teal-700">
                <Paperclip size={12} /> {extraccion.nombre_archivo}
              </span>
            </p>
            {extraccion.duplicado && (
              <p className="flex items-start gap-1.5 text-red-700">
                <AlertTriangle size={13} className="mt-0.5 shrink-0" />
                Este proveedor ya tiene cargada la factura N° {extraccion.duplicado.numero} del {formatFecha(extraccion.duplicado.fecha)}
                {extraccion.duplicado.estado === 'anulada' ? ' (anulada)' : ''}. No se puede guardar dos veces.
              </p>
            )}
            {(extraccion.avisos || []).map((a, i) => (
              <p key={i} className="flex items-start gap-1.5 text-amber-700">
                <AlertTriangle size={13} className="mt-0.5 shrink-0" /> {a}
              </p>
            ))}
            {formFactura.lineas.some((l) => !l.producto_id && !l.categoria_id) && (
              <p className="text-teal-800">Las líneas marcadas no coinciden con ningún producto del sistema: elige uno existente, créalo o, si es un gasto o servicio, elige solo su categoría.</p>
            )}
          </div>
        )}
        {/* ---- Encabezado: a quién, con qué número y en qué fecha ---- */}
        <div className={`grid gap-4 ${enPantalla ? 'sm:grid-cols-2' : ''}`}>
          <div className="bg-slate-50 rounded-md p-3">
            <p className="text-[11px] font-semibold uppercase tracking-wide text-slate-400 mb-1">
              {parte === 'proveedor' ? 'Factura de' : 'Facturar a'}
            </p>
            <p className="text-slate-800 font-medium">{entidad?.nombre || '—'}</p>
            {entidad?.rif && <p className="text-xs text-slate-500">RIF {entidad.rif}</p>}
            {esNota && facturaAfectada && (
              <p className="text-xs text-slate-500 mt-2">
                {TIPO_DOCUMENTO_LABEL[tipoDocumento]} sobre la factura{' '}
                <strong className="text-slate-700">{facturaAfectada.numero}</strong> del {formatFecha(facturaAfectada.fecha)}
              </p>
            )}
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs text-slate-500 mb-1">{esNota ? `N° de ${TIPO_DOCUMENTO_LABEL[tipoDocumento].toLowerCase()}` : CP.numeroLabel}</label>
              <input
                value={formFactura.numero}
                required={!CP.autoNumero}
                placeholder={CP.autoNumero ? '' : 'N° de la factura del proveedor'}
                onChange={(e) => setFormFactura({ ...formFactura, numero: e.target.value })}
                className="border rounded-md px-2 py-1.5 w-full text-sm"
              />
            </div>
            <div>
              <label className="block text-xs text-slate-500 mb-1">Fecha</label>
              <input
                type="date"
                value={formFactura.fecha}
                onChange={(e) => cambiarFechaFactura(e.target.value)}
                className="border rounded-md px-2 py-1.5 w-full text-sm"
              />
            </div>
            {usaCanal && (
              <div>
                <label className="block text-xs text-slate-500 mb-1">Canal</label>
                <select
                  value={formFactura.canal}
                  onChange={(e) => setFormFactura({ ...formFactura, canal: e.target.value })}
                  className="border rounded-md px-2 py-1.5 w-full text-sm"
                >
                  <option value="tienda">Tienda</option>
                  <option value="delivery">Delivery</option>
                </select>
              </div>
            )}
            <div>
              <label className="block text-xs text-slate-500 mb-1">Moneda</label>
              <select
                value={formFactura.moneda}
                onChange={(e) => setFormFactura({ ...formFactura, moneda: e.target.value })}
                className="border rounded-md px-2 py-1.5 w-full text-sm"
              >
                <option value="USD">USD</option>
                <option value="BCV">BCV</option>
                <option value="BS">BS</option>
              </select>
              {formFactura.moneda === 'BS' && (
                <label
                  className="mt-1.5 flex items-center gap-2 text-xs text-slate-600"
                  title="La base se paga a la tasa BCV del día del pago; el IVA y las retenciones quedan fijos en Bs de la emisión"
                >
                  <input
                    type="checkbox"
                    checked={!!formFactura.indexada}
                    onChange={(e) => setFormFactura({ ...formFactura, indexada: e.target.checked })}
                    className="w-4 h-4 accent-primary-600"
                  />
                  Indexada a BCV
                </label>
              )}
            </div>
            {parte === 'proveedor' && (
              <div>
                <label className="block text-xs text-slate-500 mb-1">Días de crédito</label>
                <div className="grid grid-cols-2 gap-2">
                  <input
                    type="number" min="0"
                    value={formFactura.dias_credito}
                    onChange={(e) => setFormFactura({ ...formFactura, dias_credito: e.target.value })}
                    className="border rounded-md px-2 py-1.5 w-full text-sm"
                  />
                  <input
                    type="date"
                    title="Vencimiento"
                    value={vencimientoFactura}
                    onChange={(e) => {
                      const vencimiento = e.target.value;
                      if (!vencimiento) { setFormFactura({ ...formFactura, dias_credito: '' }); return; }
                      const dias = diasEntre(formFactura.fecha, vencimiento);
                      setFormFactura({ ...formFactura, dias_credito: Math.max(0, dias) });
                    }}
                    className="border rounded-md px-2 py-1.5 w-full text-sm"
                  />
                </div>
              </div>
            )}
          </div>
        </div>

        {facturaMoneda !== 'USD' && (
          <div className="bg-slate-50 rounded-md p-3 space-y-2 text-xs">
            <p className="text-slate-500">
              Tasas del {formatFecha(formFactura.fecha)}:{' '}
              BCV <strong>{formFactura.tasasDia?.bcv != null ? formatMonto(formFactura.tasasDia.bcv) : '—'}</strong> ·{' '}
              mercado <strong>{formFactura.tasasDia?.mercado != null ? formatMonto(formFactura.tasasDia.mercado) : '—'}</strong>
            </p>
            {facturaMoneda === 'BCV' && formFactura.tasasDia?.bcv == null && (
              <p className="text-amber-600">Esa fecha no tiene tasa BCV cargada. Cargala en Tasas de Cambio antes de guardar.</p>
            )}
            <div className="flex items-center gap-2">
              <label className="text-slate-500 whitespace-nowrap">Tasa USD (opcional)</label>
              <input
                type="number" step="0.0001" min="0" value={formFactura.tasa_usd}
                placeholder={formFactura.tasasDia?.mercado != null ? formatMonto(formFactura.tasasDia.mercado) : 'sin tasa ese día'}
                onChange={(e) => setFormFactura({ ...formFactura, tasa_usd: e.target.value })}
                className="border rounded-md px-2 py-1 w-32 text-sm"
              />
              <span className="text-slate-400">solo si querés forzar otra tasa USD</span>
            </div>
          </div>
        )}

        {/* ---- Líneas: mismo orden que el cuerpo de una factura impresa ---- */}
        <div>
          <div className="flex items-center justify-between mb-1">
            <label className="text-xs text-slate-500">
              {esNota ? 'Líneas de la nota' : 'Líneas'} (precios en {MONEDA_LABEL[formFactura.moneda]})
            </label>
            <button type="button" onClick={agregarLinea} title="Agregar línea" className="text-slate-400 hover:text-primary-600 transition-colors">
              <Plus size={16} />
            </button>
          </div>
          <div className="hidden sm:grid grid-cols-12 gap-2 px-2 pb-1 text-[11px] font-semibold uppercase tracking-wide text-slate-400">
            <div className="col-span-3">Producto</div>
            <div className="col-span-4">Descripción</div>
            <div className="col-span-1 text-right">Cant.</div>
            <div className="col-span-2 text-right">Precio</div>
            <div className="col-span-2 text-right">Monto</div>
          </div>
          <div className="space-y-2">
            {formFactura.lineas.map((l, i) => (
              <div key={i} className={`grid grid-cols-12 gap-2 items-start rounded-md p-2 ${desdeIA && !l.producto_id && !l.categoria_id ? 'bg-amber-50 ring-1 ring-amber-300' : 'bg-slate-50'}`}>
                <div className="col-span-12 sm:col-span-3">
                  <AutocompleteProducto
                    productos={productos}
                    value={l.producto_id}
                    onChange={(pid) => elegirProducto(i, pid)}
                    onCrear={(nombre) => { setQuickError(''); setQuickProd({ i, nombre, unidad_medida: l.unidad_ia || 'Unidad', categoria_id: categoriaHabitual || '' }); }}
                    placeholder={desdeIA ? 'Producto…' : 'Producto (o dejar vacío)'}
                  />
                  {desdeIA && !l.producto_id && !l.categoria_id && (
                    <button
                      type="button"
                      onClick={() => { setQuickError(''); setQuickProd({ i, nombre: l.descripcion, unidad_medida: l.unidad_ia || 'Unidad', categoria_id: categoriaHabitual || '' }); }}
                      className="mt-1 text-[11px] text-primary-600 hover:underline text-left"
                    >
                      Crear producto “{l.descripcion}”
                    </button>
                  )}
                </div>
                <div className="col-span-12 sm:col-span-4">
                  <input
                    value={l.descripcion}
                    onChange={(e) => setLinea(i, { descripcion: e.target.value })}
                    placeholder="Descripción"
                    className="border rounded-md px-2 py-1.5 w-full text-sm"
                  />
                  {!l.producto_id && (
                    <AutocompleteCategoria
                      className="mt-1"
                      categorias={categoriasLinea}
                      value={l.categoria_id}
                      onChange={(id) => setLinea(i, { categoria_id: id })}
                      onNueva={puedeCrearCategoria ? (texto) => { setQuickError(''); setQuickCat({ i, nombre: texto || l.descripcion || '' }); } : undefined}
                      placeholder={CP.catPlaceholder}
                    />
                  )}
                </div>
                <div className="col-span-3 sm:col-span-1">
                  <input
                    type="number" step="0.0001" min="0"
                    value={l.cantidad}
                    onChange={(e) => setLinea(i, { cantidad: e.target.value })}
                    placeholder="Cant."
                    className="border rounded-md px-2 py-1.5 w-full text-sm text-right"
                  />
                </div>
                <div className="col-span-4 sm:col-span-2">
                  <input
                    type="number" step="0.0001" min="0"
                    value={l.precio_unitario}
                    onChange={(e) => setLinea(i, { precio_unitario: e.target.value })}
                    placeholder="Precio"
                    className="border rounded-md px-2 py-1.5 w-full text-sm text-right"
                  />
                </div>
                <div className="col-span-5 sm:col-span-2 flex items-center justify-end gap-2 pt-1.5">
                  <span className="text-sm text-slate-700">
                    {pref(formFactura.moneda)}{formatMonto((Number(l.cantidad) || 0) * (Number(l.precio_unitario) || 0))}
                  </span>
                  <button type="button" onClick={() => quitarLinea(i)} className="text-slate-400 hover:text-red-600" title="Quitar línea">
                    <X size={15} />
                  </button>
                </div>
                {(() => {
                  const v = parte === 'proveedor' ? variacionPrecio(l) : null;
                  if (!v) return null;
                  return (
                    <p className="col-span-12 flex items-center gap-1.5 text-[11px] text-amber-700">
                      <AlertTriangle size={12} />
                      {v.pct > 0 ? '+' : ''}{formatMonto(v.pct, 1)} % vs. el último costo
                      ({v.ultimo.moneda === 'USD' ? '$ ' : 'Bs '}{formatMonto(v.ultimo.costo_unitario, 4)} el {formatFecha(v.ultimo.fecha)})
                    </p>
                  );
                })()}
                {formFactura.es_fiscal && (
                  <label className="col-span-12 flex items-center gap-2 text-xs text-slate-500">
                    <input
                      type="checkbox"
                      checked={!!l.exento}
                      onChange={(e) => setLinea(i, { exento: e.target.checked })}
                      className="w-3.5 h-3.5 accent-primary-600"
                    />
                    Exento de IVA
                  </label>
                )}
              </div>
            ))}
          </div>
        </div>

        {/* ---- Notas y totales, como el pie de la factura impresa ---- */}
        <div className="grid gap-4 sm:grid-cols-2">
          <div>
            <label className="block text-xs text-slate-500 mb-1">Notas (opcional)</label>
            <input
              value={formFactura.notas}
              onChange={(e) => setFormFactura({ ...formFactura, notas: e.target.value })}
              className="border rounded-md px-2 py-1.5 w-full text-sm"
            />
          </div>
          <div className="text-sm text-slate-600 sm:text-right space-y-0.5">
            <p>Subtotal: <strong className="text-slate-800">{pref(formFactura.moneda)}{formatMonto(totalFactura)}</strong></p>
            {formFactura.moneda === 'BS' && formFactura.indexada && (
              <p className="text-xs text-sky-700">
                {Number(formFactura.tasasDia?.bcv) > 0
                  ? `Indexada: base = BCV$ ${formatMonto(totalFactura / Number(formFactura.tasasDia.bcv))} (BCV de la fecha ${formatMonto(formFactura.tasasDia.bcv)}); se paga a la BCV del día del pago. IVA y retenciones fijos en Bs.`
                  : 'Indexada: falta la tasa BCV de la fecha de la factura (cárgala en Tasas de cambio).'}
              </p>
            )}
            {formFactura.es_fiscal && montoExentoEfectivo > 0 && (
              <p className="text-slate-500">Exento: {pref(formFactura.moneda)}{formatMonto(montoExentoEfectivo)}</p>
            )}
            {formFactura.es_fiscal && (
              <p className="text-slate-500">
                IVA ({formatMonto(alicuotaEfectiva)}%): {pref(formFactura.moneda)}{formatMonto(ivaCalculado)}
              </p>
            )}
            <p className="text-base font-semibold text-slate-800 pt-1">
              Total: {pref(formFactura.moneda)}{formatMonto(totalFactura + ivaCalculado)}
            </p>
            {formFactura.moneda !== 'USD' && totalFacturaUsd != null && (
              <p className="text-xs text-slate-400">≈ USD $ {formatMonto(totalFacturaUsd)}</p>
            )}
          </div>
        </div>

        <div className="border rounded-md overflow-hidden">
          <div
            {...(formFactura.es_fiscal ? filaClickeable(() => setFiscalAbierto((v) => !v)) : {})}
            role={formFactura.es_fiscal ? 'button' : undefined}
            className={`flex items-center justify-between px-3 py-2 bg-slate-50 ${formFactura.es_fiscal ? `hover:bg-slate-100 cursor-pointer ${FOCO_FILA}` : ''}`}
          >
            <label className="flex items-center gap-2 text-sm font-medium text-slate-700" onClick={(e) => e.stopPropagation()}>
              <input
                type="checkbox"
                checked={formFactura.es_fiscal}
                onChange={(e) => {
                  // Al tildar se despliega el bloque; al destildar se limpia
                  // todo el desglose fiscal y queda solo el encabezado.
                  setFiscalAbierto(true);
                  if (!e.target.checked) setFiscalManual(false);
                  setFormFactura({
                    ...formFactura,
                    ...(e.target.checked ? {} : { ...FISCAL_VACIO, retencion_municipal_porcentaje: pctMunicipalEmpresa }),
                    es_fiscal: e.target.checked,
                    // El correlativo sugerido se repone al volver a tildar.
                    numero_control: e.target.checked ? (formFactura.numero_control || controlSugerido) : '',
                    tipo_documento: e.target.checked ? formFactura.tipo_documento : 'factura',
                    documento_afectado_id: e.target.checked ? formFactura.documento_afectado_id : ''
                  });
                }}
              />
              Documento fiscal
            </label>
            {formFactura.es_fiscal
              ? (fiscalAbierto ? <ChevronDown size={16} className="text-slate-400" /> : <ChevronRight size={16} className="text-slate-400" />)
              : <span className="text-xs text-slate-400">tildá para cargar el desglose fiscal</span>}
          </div>
          {!formFactura.es_fiscal && (
            <p className="px-3 py-2 text-xs text-slate-500 border-t">
              Sin RIF/número de control fiscal. No le aplica IVA ni retención y queda fuera del Libro de {parte === 'proveedor' ? 'Compras' : 'Ventas'} exportable — solo en el reporte interno de documentos no fiscales.
            </p>
          )}
          {formFactura.es_fiscal && fiscalAbierto && (
          <div className="p-3 space-y-3 border-t">
          {formFactura.es_fiscal && (
            <>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                {/* Tipo de documento: una carga normal es siempre
                    "factura". Las notas entran por su propio acceso, con
                    la factura afectada ya elegida; acá solo se muestra. */}
                {esNota && (
                  <div className="col-span-2">
                    <label className="block text-xs text-slate-500 mb-1">Tipo de documento</label>
                    <p className="border border-transparent bg-slate-50 rounded-md px-2 py-1.5 text-sm text-slate-700">
                      {TIPO_DOCUMENTO_LABEL[formFactura.tipo_documento]}
                      {facturaAfectada ? ` sobre la factura ${facturaAfectada.numero}` : ''}
                    </p>
                  </div>
                )}
                <div>
                  <label className="block text-xs text-slate-500 mb-1">N° de control</label>
                  <input
                    value={formFactura.numero_control}
                    onChange={(e) => setFormFactura({ ...formFactura, numero_control: e.target.value })}
                    placeholder={parte === 'cliente' ? (controlSugerido || 'de tu talonario') : 'el impreso en la factura'}
                    className="border rounded-md px-2 py-1.5 w-full text-sm"
                  />
                  {parte === 'cliente' && (
                    <p className="text-[11px] text-slate-400 mt-1">
                      {controlSugerido
                        ? <>Sigue al último cargado.{formFactura.numero_control !== controlSugerido && (
                            <button type="button" onClick={() => setFormFactura({ ...formFactura, numero_control: controlSugerido })} className="ml-1 text-primary-600 hover:underline">
                              usar {controlSugerido}
                            </button>
                          )}</>
                        : 'Todavía no hay ninguno cargado: el que pongas arranca el correlativo.'}
                    </p>
                  )}
                </div>
                <div>
                  <label className="block text-xs text-slate-500 mb-1">Tipo de operación</label>
                  <select
                    value={formFactura.tipo_operacion}
                    onChange={(e) => setFormFactura({ ...formFactura, tipo_operacion: e.target.value })}
                    className="border rounded-md px-2 py-1.5 w-full text-sm"
                  >
                    <option value="interna">Interna</option>
                    <option value="importacion">Importación</option>
                  </select>
                </div>
              </div>

              {/* Los cuatro montos salen de las líneas (#34): exento = líneas
                  marcadas, base = resto, IVA = base × alícuota de la empresa.
                  "Corregir a mano" los habilita para cuadrar con el papel. */}
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                <div>
                  <label className="block text-xs text-slate-500 mb-1">Monto exento</label>
                  {fiscalManual ? (
                    <input
                      type="number" step="0.01" min="0"
                      value={formFactura.monto_exento}
                      onChange={(e) => setFormFactura({ ...formFactura, monto_exento: e.target.value })}
                      placeholder="0"
                      className="border rounded-md px-2 py-1.5 w-full text-sm"
                    />
                  ) : (
                    <p className="border border-transparent bg-slate-50 rounded-md px-2 py-1.5 text-sm text-slate-700">
                      {pref(formFactura.moneda)}{formatMonto(montoExentoEfectivo)}
                    </p>
                  )}
                </div>
                <div>
                  <label className="block text-xs text-slate-500 mb-1">Base imponible</label>
                  {fiscalManual ? (
                    <input
                      type="number" step="0.01" min="0"
                      value={formFactura.base_imponible}
                      onChange={(e) => setFormFactura({ ...formFactura, base_imponible: e.target.value })}
                      placeholder={formatMonto(baseImponibleSugerida)}
                      className="border rounded-md px-2 py-1.5 w-full text-sm"
                    />
                  ) : (
                    <p className="border border-transparent bg-slate-50 rounded-md px-2 py-1.5 text-sm text-slate-700">
                      {pref(formFactura.moneda)}{formatMonto(baseImponibleEfectiva)}
                    </p>
                  )}
                </div>
                <div>
                  <label className="block text-xs text-slate-500 mb-1">Alícuota IVA %</label>
                  {fiscalManual ? (
                    <input
                      type="number" step="0.01" min="0"
                      value={formFactura.alicuota_iva}
                      onChange={(e) => setFormFactura({ ...formFactura, alicuota_iva: e.target.value })}
                      placeholder={String(alicuotaEmpresa)}
                      className="border rounded-md px-2 py-1.5 w-full text-sm"
                    />
                  ) : (
                    <p className="border border-transparent bg-slate-50 rounded-md px-2 py-1.5 text-sm text-slate-700">
                      {formatMonto(alicuotaEfectiva)} %
                    </p>
                  )}
                </div>
                <div>
                  <label className="block text-xs text-slate-500 mb-1">Monto IVA</label>
                  {fiscalManual ? (
                    <input
                      type="number" step="0.01" min="0"
                      value={formFactura.monto_iva}
                      onChange={(e) => setFormFactura({ ...formFactura, monto_iva: e.target.value })}
                      placeholder={formatMonto(ivaCalculado)}
                      className="border rounded-md px-2 py-1.5 w-full text-sm"
                    />
                  ) : (
                    <p className="border border-transparent bg-slate-50 rounded-md px-2 py-1.5 text-sm font-medium text-slate-700">
                      {pref(formFactura.moneda)}{formatMonto(ivaCalculado)}
                    </p>
                  )}
                </div>
              </div>
              <div className="flex items-center justify-between gap-2">
                <p className="text-[11px] text-slate-400">
                  {fiscalManual
                    ? 'Valores cargados a mano: se guardan tal cual, sin recalcularse desde las líneas.'
                    : `Calculado desde las líneas — exento = líneas marcadas, base = resto, IVA = base × ${formatMonto(alicuotaEmpresa)} % (alícuota de la empresa).`}
                </p>
                <button
                  type="button"
                  onClick={() => {
                    if (fiscalManual) {
                      // Volver a fórmula: se limpian los overrides.
                      setFiscalManual(false);
                      setFormFactura({ ...formFactura, monto_exento: '', base_imponible: '', alicuota_iva: '', monto_iva: '' });
                    } else {
                      // Arrancar el manual desde lo que hoy muestra la fórmula.
                      setFiscalManual(true);
                      setFormFactura({
                        ...formFactura,
                        monto_exento: String(montoExentoEfectivo),
                        base_imponible: String(baseImponibleEfectiva),
                        alicuota_iva: String(alicuotaEfectiva),
                        monto_iva: String(ivaCalculado)
                      });
                    }
                  }}
                  className="text-xs text-primary-600 hover:underline whitespace-nowrap"
                >
                  {fiscalManual ? 'Volver al cálculo automático' : 'Corregir a mano'}
                </button>
              </div>

              <label className="flex items-center gap-2 text-xs text-slate-600">
                <input
                  type="checkbox"
                  checked={formFactura.aplicaIgtf}
                  onChange={(e) => setFormFactura({ ...formFactura, aplicaIgtf: e.target.checked })}
                />
                Aplica IGTF
              </label>
              {formFactura.aplicaIgtf && (
                <div className="grid grid-cols-3 gap-3">
                  <div>
                    <label className="block text-xs text-slate-500 mb-1">Base IGTF</label>
                    <input
                      type="number" step="0.01" min="0"
                      value={formFactura.base_igtf}
                      onChange={(e) => setFormFactura({ ...formFactura, base_igtf: e.target.value })}
                      className="border rounded-md px-2 py-1.5 w-full text-sm"
                    />
                  </div>
                  <div>
                    <label className="block text-xs text-slate-500 mb-1">Alícuota IGTF %</label>
                    <input
                      type="number" step="0.01" min="0"
                      value={formFactura.alicuota_igtf}
                      onChange={(e) => setFormFactura({ ...formFactura, alicuota_igtf: e.target.value })}
                      className="border rounded-md px-2 py-1.5 w-full text-sm"
                    />
                  </div>
                  <div>
                    <label className="block text-xs text-slate-500 mb-1">Monto IGTF</label>
                    <input
                      type="number" step="0.01" min="0"
                      value={formFactura.monto_igtf}
                      onChange={(e) => setFormFactura({ ...formFactura, monto_igtf: e.target.value })}
                      className="border rounded-md px-2 py-1.5 w-full text-sm"
                    />
                  </div>
                </div>
              )}

              {parte === 'cliente' && (
                <>
                  <button
                    type="button"
                    onClick={() => setFormFactura({
                      ...formFactura,
                      mostrarRetencion: !formFactura.mostrarRetencion,
                      // Al abrirla se proponen fecha (la de la factura) y
                      // monto (IVA × 75%); el N° lo trae el comprobante
                      // que emite el cliente, así que ese se teclea.
                      ...(formFactura.mostrarRetencion ? {} : {
                        retencion_fecha: formFactura.retencion_fecha || formFactura.fecha,
                        retencion_iva_monto: formFactura.retencion_iva_monto || String(retencionVentaSugerida)
                      })
                    })}
                    className="text-xs text-primary-600 hover:underline"
                  >
                    {formFactura.mostrarRetencion ? '− Ocultar retención' : '+ Retención de IVA del cliente'}
                  </button>
                  {formFactura.mostrarRetencion && (
                    <div className="grid grid-cols-3 gap-3">
                      <div>
                        <label className="block text-xs text-slate-500 mb-1">Fecha</label>
                        <input
                          type="date"
                          value={formFactura.retencion_fecha}
                          onChange={(e) => setFormFactura({ ...formFactura, retencion_fecha: e.target.value })}
                          className="border rounded-md px-2 py-1.5 w-full text-sm"
                        />
                      </div>
                      <div>
                        <label className="block text-xs text-slate-500 mb-1">N° comprobante</label>
                        <input
                          value={formFactura.retencion_numero_comprobante}
                          onChange={(e) => setFormFactura({ ...formFactura, retencion_numero_comprobante: e.target.value })}
                          className="border rounded-md px-2 py-1.5 w-full text-sm"
                        />
                      </div>
                      <div>
                        <label className="block text-xs text-slate-500 mb-1">Monto retenido</label>
                        <input
                          type="number" step="0.01" min="0"
                          value={formFactura.retencion_iva_monto}
                          onChange={(e) => setFormFactura({ ...formFactura, retencion_iva_monto: e.target.value })}
                          placeholder={formatMonto(retencionVentaSugerida)}
                          className="border rounded-md px-2 py-1.5 w-full text-sm"
                        />
                      </div>
                      <p className="col-span-3 text-[11px] text-slate-400">
                        El agente de retención es el cliente: el N° de comprobante se copia del papel que él emite.
                        Sugerido: IVA {pref(formFactura.moneda)}{formatMonto(ivaCalculado)} × 75% = {pref(formFactura.moneda)}{formatMonto(retencionVentaSugerida)}.
                      </p>
                    </div>
                  )}
                </>
              )}
            </>
          )}
          </div>
          )}
        </div>

        {formFactura.es_fiscal && parte === 'proveedor' && empresaActivaData &&
          (empresaActivaData.agente_retencion || empresaActivaData.agente_retencion_islr || empresaActivaData.agente_retencion_municipal) && (
          <div className="border rounded-md overflow-hidden">
            <div
              {...filaClickeable(() => setRetencionAbierta((v) => !v))}
              role="button"
              className={`flex items-center justify-between px-3 py-2 bg-slate-50 hover:bg-slate-100 cursor-pointer ${FOCO_FILA}`}
            >
              <span className="text-sm font-medium text-slate-700">Retención (calculada)</span>
              {retencionAbierta ? <ChevronDown size={16} className="text-slate-400" /> : <ChevronRight size={16} className="text-slate-400" />}
            </div>
            {retencionAbierta && (
            <div className="p-3 space-y-3 border-t">
                    {!!empresaActivaData.agente_retencion && (
                      <div className="grid grid-cols-3 gap-3 items-end">
                        <label className="flex items-center gap-2 text-sm text-slate-600 col-span-3 sm:col-span-1">
                          <input
                            type="checkbox"
                            checked={formFactura.retencion_iva_aplica}
                            disabled={!!formFactura.retenciones?.find((r) => r.tipo === 'iva')}
                            onChange={(e) => setFormFactura({ ...formFactura, retencion_iva_aplica: e.target.checked })}
                            className="w-4 h-4 accent-primary-600"
                          />
                          Retiene IVA
                        </label>
                        <div>
                          <label className="block text-xs text-slate-500 mb-1">% retenido</label>
                          <select
                            value={formFactura.retencion_iva_porcentaje}
                            onChange={(e) => setFormFactura({ ...formFactura, retencion_iva_porcentaje: e.target.value })}
                            disabled={!formFactura.retencion_iva_aplica}
                            className="border rounded-md px-2 py-1.5 w-full text-sm"
                          >
                            <option value="75">75%</option>
                            <option value="100">100%</option>
                          </select>
                        </div>
                        {formFactura.retencion_iva_aplica && (
                          <p className="text-sm text-slate-600">
                            Monto retenido: <strong>{pref(formFactura.moneda)}{formatMonto((Number(ivaCalculado) || 0) * (Number(formFactura.retencion_iva_porcentaje) || 0) / 100)}</strong>
                            <span className="block text-[11px] text-slate-400">IVA {pref(formFactura.moneda)}{formatMonto(ivaCalculado)} × {formFactura.retencion_iva_porcentaje}%</span>
                          </p>
                        )}
                      </div>
                    )}

                    {!!empresaActivaData.agente_retencion_islr && (() => {
                      const islrEmitida = !!formFactura.retenciones?.find((r) => r.tipo === 'islr');
                      const codIslr = conceptosIslr.find((c) => String(c.id) === String(formFactura.retencion_islr_codigo_id));
                      const configurado = retencionesConfiguradas(entidad);
                      return (
                      <div className="grid grid-cols-3 gap-3 items-end">
                        <div className="col-span-3 sm:col-span-1">
                          <label className="block text-xs text-slate-500 mb-1">ISLR · {TIPO_PERSONA_LABEL[tipoProveedor].toLowerCase()}</label>
                          {elegirIslr && !islrEmitida ? (
                            <select
                              autoFocus
                              onBlur={() => setElegirIslr(false)}
                              value={formFactura.retencion_islr_codigo_id}
                              onChange={(e) => {
                                const cod = conceptosIslr.find((c) => String(c.id) === e.target.value);
                                setFormFactura({
                                  ...formFactura, retencion_islr_codigo_id: e.target.value,
                                  ...(cod ? valoresIslrProveedor(cod) : { retencion_islr_porcentaje: '', retencion_islr_sustraendo: '' })
                                });
                                setElegirIslr(false);
                              }}
                              className="border rounded-md px-2 py-1.5 w-full text-sm"
                            >
                              <option value="">No aplica a esta factura</option>
                              {conceptosIslr.filter((c) => c.activo || String(c.id) === String(formFactura.retencion_islr_codigo_id)).map((c) => (
                                <option key={c.id} value={c.id}>{c.concepto}</option>
                              ))}
                            </select>
                          ) : (
                            <p className="text-sm text-slate-700">
                              {codIslr ? codIslr.concepto : (formFactura.retencion_islr_codigo_id ? 'Concepto sin catálogo' : 'No se le retiene ISLR')}
                              {!islrEmitida && (
                                <button
                                  type="button"
                                  onClick={() => setElegirIslr(true)}
                                  className="block text-xs text-primary-600 hover:underline"
                                >
                                  {codIslr ? 'Usar otro concepto en esta factura' : 'Retener ISLR en esta factura'}
                                </button>
                              )}
                              {!codIslr && !configurado && (
                                <span className="block text-[11px] text-slate-400">Configúralo en el botón % Retenciones del proveedor.</span>
                              )}
                            </p>
                          )}
                        </div>
                        {formFactura.retencion_islr_codigo_id && (
                          <>
                            <div>
                              <label className="block text-xs text-slate-500 mb-1">%</label>
                              <input
                                type="number" step="0.01"
                                value={formFactura.retencion_islr_porcentaje}
                                disabled={islrEmitida}
                                onChange={(e) => setFormFactura({ ...formFactura, retencion_islr_porcentaje: e.target.value })}
                                className="border rounded-md px-2 py-1.5 w-full text-sm"
                              />
                            </div>
                            <div>
                              <label className="block text-xs text-slate-500 mb-1">Sustraendo</label>
                              <input
                                type="number" step="0.01"
                                value={formFactura.retencion_islr_sustraendo}
                                disabled={islrEmitida}
                                onChange={(e) => setFormFactura({ ...formFactura, retencion_islr_sustraendo: e.target.value })}
                                className="border rounded-md px-2 py-1.5 w-full text-sm"
                              />
                            </div>
                            <p className="text-sm text-slate-600 col-span-3">
                              Monto retenido: <strong>{pref(formFactura.moneda)}{formatMonto(Math.max(baseImponibleEfectiva * (Number(formFactura.retencion_islr_porcentaje) || 0) / 100 - (Number(formFactura.retencion_islr_sustraendo) || 0), 0))}</strong>
                              <span className="block text-[11px] text-slate-400">
                                Base {pref(formFactura.moneda)}{formatMonto(baseImponibleEfectiva)} × {formFactura.retencion_islr_porcentaje || 0}% − sustraendo {formatMonto(Number(formFactura.retencion_islr_sustraendo) || 0)}
                              </span>
                            </p>
                          </>
                        )}
                      </div>
                      );
                    })()}

                    {!!empresaActivaData.agente_retencion_municipal && (
                      <div className="grid grid-cols-3 gap-3 items-end">
                        <label className="flex items-center gap-2 text-sm text-slate-600 col-span-3 sm:col-span-1">
                          <input
                            type="checkbox"
                            checked={formFactura.retencion_municipal_aplica}
                            disabled={!!formFactura.retenciones?.find((r) => r.tipo === 'municipal')}
                            onChange={(e) => setFormFactura({ ...formFactura, retencion_municipal_aplica: e.target.checked })}
                            className="w-4 h-4 accent-primary-600"
                          />
                          Retiene Municipal
                        </label>
                        <div>
                          <label className="block text-xs text-slate-500 mb-1">%</label>
                          <input
                            type="number" step="0.01"
                            value={formFactura.retencion_municipal_porcentaje}
                            disabled={!formFactura.retencion_municipal_aplica}
                            onChange={(e) => setFormFactura({ ...formFactura, retencion_municipal_porcentaje: e.target.value })}
                            className="border rounded-md px-2 py-1.5 w-full text-sm"
                          />
                        </div>
                        {formFactura.retencion_municipal_aplica && (
                          <p className="text-sm text-slate-600">
                            Monto retenido: <strong>{pref(formFactura.moneda)}{formatMonto(baseImponibleEfectiva * (Number(formFactura.retencion_municipal_porcentaje) || 0) / 100)}</strong>
                            <span className="block text-[11px] text-slate-400">Base {pref(formFactura.moneda)}{formatMonto(baseImponibleEfectiva)} × {formFactura.retencion_municipal_porcentaje || 0}%</span>
                          </p>
                        )}
                      </div>
                    )}
            </div>
            )}
          </div>
        )}

        {errorFactura && <p className="text-red-600 text-sm">{errorFactura}</p>}
        <div className="flex justify-end gap-2">
          <button type="button" onClick={onCancelar} className="px-4 py-2 rounded-md text-sm text-slate-600 hover:bg-slate-100">
            Cancelar
          </button>
          <button type="submit" disabled={guardando || (desdeIA && !!extraccion.duplicado)} className="bg-primary-600 text-white px-4 py-2 rounded-md text-sm hover:bg-primary-700 disabled:opacity-60">
            {formFactura.editandoId
              ? 'Guardar cambios'
              : esNota ? `Crear ${TIPO_DOCUMENTO_LABEL[tipoDocumento].toLowerCase()}` : (CP.autoNumero ? 'Crear factura' : 'Guardar factura de compra')}
          </button>
        </div>
      </form>

      {quickProd && (
        <MiniModal title="Nuevo producto" onClose={() => setQuickProd(null)}>
          <form onSubmit={guardarProductoRapido} className="space-y-2">
            <input
              autoFocus
              value={quickProd.nombre}
              onChange={(e) => setQuickProd({ ...quickProd, nombre: e.target.value })}
              placeholder="Nombre del producto"
              className="border rounded-md px-2 py-1.5 w-full text-sm"
            />
            <select
              value={quickProd.unidad_medida}
              onChange={(e) => setQuickProd({ ...quickProd, unidad_medida: e.target.value })}
              className="border rounded-md px-2 py-1.5 w-full text-sm"
            >
              <option value="Unidad">Unidad</option>
              <option value="Kg">Kg</option>
              <option value="Lt">Lt</option>
            </select>
            {parte === 'proveedor' && (
              <select
                value={quickProd.categoria_id || ''}
                onChange={(e) => setQuickProd({ ...quickProd, categoria_id: e.target.value })}
                className="border rounded-md px-2 py-1.5 w-full text-sm"
              >
                <option value="">Sin categoría contable</option>
                {categorias.filter((c) => c.tipo === 'egreso').map((c) => (
                  <option key={c.id} value={c.id}>{c.codigo ? `${c.codigo} ` : ''}{c.padre_nombre ? `${c.padre_nombre} › ` : ''}{c.nombre}</option>
                ))}
              </select>
            )}
            {parte === 'proveedor' && categoriaHabitual && String(quickProd.categoria_id) === String(categoriaHabitual) && (
              <p className="text-[11px] text-slate-400">Categoría propuesta: la que más usan los productos de este proveedor.</p>
            )}
            <p className="text-[11px] text-slate-400">
              Se crea como {parte === 'proveedor' ? '“solo compra”' : '“solo venta”'}. El resto se completa después en Productos.
            </p>
            {quickError && <p className="text-red-600 text-xs">{quickError}</p>}
            <button type="submit" className="bg-primary-600 text-white rounded-md py-1.5 w-full text-sm hover:bg-primary-700">
              Crear y usar
            </button>
          </form>
        </MiniModal>
      )}

      {quickCat && (
        <MiniModal title="Nueva categoría" onClose={() => setQuickCat(null)}>
          <form onSubmit={guardarCategoriaRapida} className="space-y-2">
            <input
              autoFocus
              value={quickCat.nombre}
              onChange={(e) => setQuickCat({ ...quickCat, nombre: e.target.value })}
              placeholder="Nombre de la categoría"
              className="border rounded-md px-2 py-1.5 w-full text-sm"
            />
            <p className="text-[11px] text-slate-400">
              Categoría de {CP.catTipo}. Se puede reorganizar después en Categorías.
            </p>
            {quickError && <p className="text-red-600 text-xs">{quickError}</p>}
            <button type="submit" className="bg-primary-600 text-white rounded-md py-1.5 w-full text-sm hover:bg-primary-700">
              Crear y usar
            </button>
          </form>
        </MiniModal>
      )}
    </>
  );
}
